import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    IconButton,
    Typography,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Card,
    CardContent,
    InputAdornment,
    Paper,
    Divider,
    Avatar,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Autocomplete,
    Snackbar,
    Alert,
    CircularProgress,
    Checkbox,
    Switch,
    FormControlLabel
} from '@mui/material';
import {
    Close as CloseIcon,
    Event as EventIcon,
    Person as PersonIcon,
    Business as BusinessIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    LocationOn as LocationIcon,
    AttachMoney as PriceIcon,
    Inventory as InventoryIcon,
    Category as CategoryIcon,
    Notes as NotesIcon,
    Schedule as ScheduleIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    List as ListIcon,
    Search as SearchIcon,
    CheckCircle as CheckCircleIcon,
    Description as DescriptionIcon,
    Warning as WarningIcon
} from '@mui/icons-material';
import { postData, updateData, fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';

const EstimateModal = ({
    open,
    onClose,
    onSave,
    estimateData = null,
    mode = 'create',
    customers = [],
    items = [],
    enquiries = []
}) => {
    const { companyId, branchId, token, userId, permissions } = useAuth();

    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        EstimateNo: '',
        EstimateNumber: '',
        BillNumber: '',
        BillPrefix: '',
        CustomerId: '',
        CustomerName: '',
        CustomerTypeId: 1,
        EnquiryId: '',
        EnquiryNo: '',
        ContactNumber1: '',
        ContactNumber2: '',
        GSTNumber: '',
        AddressLine1: '',
        AddressLine2: '',
        Location: '',
        PinCode: '',
        BillDate: '',
        EventName: '',
        EventLocation: '',
        EventAddress: '',
        EventDays: '',
        RentDays: '',
        ExpectedTime: '',
        ProgramDate: '',
        DeliveryDate: '',
        DeliveryReturnDate: '',
        Amount: '0',
        DiscountAmount: '0',
        CGSTAmount: '0',
        SGSTAmount: '0',
        IGSTAmount: '0',
        TransportCharge: '0',
        RoundOff: '0',
        TotalAmount: '0',
        AdvanceAmount: '0',
        BalanceAmount: '0',
        PaymentMode: 1,
        Remarks: '',
        FinYear: new Date().getFullYear().toString(),
        items: []
    });

    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState('');
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
    const [isGST, setIsGST] = useState(true);
    const [isBlacklisted, setIsBlacklisted] = useState(false);

    // Get Items modal state
    const [getItemsModalOpen, setGetItemsModalOpen] = useState(false);
    const [getItemsLoading, setGetItemsLoading] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [getItemsSearchValue, setGetItemsSearchValue] = useState('');
    const [currentItemsPage, setCurrentItemsPage] = useState(1);
    const [itemsPerPage] = useState(50);
    const [addingItemsLoading, setAddingItemsLoading] = useState(false);

    // Fetch bill number for estimate
    const fetchBillNumberForEstimate = async () => {
        try {
            const response = await fetchData('EstimateApi', `/MaxBillno/${companyId}/${branchId}`, token);
            console.log('Bill number response:', response);
            const data = response.response_data || response || {};
            const billNumber = data.BillNumber || data.billNumber || 1;
            const billPrefix = data.BillPrefix || data.billPrefix || '';
            const finYear = data.FinYear || data.finYear || '';

            const estimateNo = `${billPrefix}/${String(billNumber)}/${finYear}`;
            console.log('Generated EstimateNo:', estimateNo);

            setFormData(prev => ({
                ...prev,
                BillNumber: billNumber,
                BillPrefix: billPrefix,
                EstimateNo: estimateNo,
                FinYear: finYear
            }));
        } catch (error) {
            console.error('Error fetching bill number for estimate:', error);
        }
    };

    // Fetch branch data for IsGST setting
    const fetchBranchData = async () => {
        try {
            const response = await fetchData('BranchApi', `/BranchDetails/${branchId}`, token);
            console.log('Branch data response:', response);
            const branchData = response.response_data || response || {};
            const isGSTEnabled = branchData.IsGST !== undefined ? branchData.IsGST : true;
            setIsGST(isGSTEnabled);
        } catch (error) {
            console.error('Error fetching branch data:', error);
            setIsGST(true); // Default to true if fetch fails
        }
    };

    useEffect(() => {
        if (open) {
            if (estimateData) {
                console.log('Loading estimateData:', estimateData);
                setFormData({
                    Id: estimateData.Id || '',
                    EstimateNo: estimateData.EstimateNo || '',
                    EstimateNumber: estimateData.EstimateNumber || '',
                    BillNumber: estimateData.BillNumber || '',
                    BillPrefix: estimateData.BillPrefix || '',
                    CustomerId: estimateData.CustomerId || '',
                    CustomerName: estimateData.CustomerName || '',
                    CustomerTypeId: estimateData.CustomerTypeId || 1,
                    EnquiryId: estimateData.EnquiryId || '',
                    EnquiryNo: estimateData.EnquiryNo || '',
                    EnquiryDate: estimateData.EnquiryDate || '',
                    ContactNumber1: estimateData.ContactNumber1 || '',
                    ContactNumber2: estimateData.ContactNumber2 || '',
                    GSTNumber: estimateData.GSTNumber || '',
                    AddressLine1: estimateData.AddressLine1 || '',
                    AddressLine2: estimateData.AddressLine2 || '',
                    Location: estimateData.Location || '',
                    PinCode: estimateData.PinCode || '',
                    BillDate: estimateData.BillDate ? estimateData.BillDate.split('T')[0] : new Date().toISOString().split('T')[0],
                    EventName: estimateData.EventName || '',
                    EventLocation: estimateData.EventLocation || '',
                    EventAddress: estimateData.EventAddress || '',
                    EventDays: estimateData.EventDays || '',
                    RentDays : estimateData.estimateData || '',
                    ExpectedTime: estimateData.ExpectedTime || '',
                    ProgramDate: estimateData.ProgramDate ? estimateData.ProgramDate.split('T')[0] : '',
                    DeliveryDate: estimateData.DeliveryDate ? estimateData.DeliveryDate.split('T')[0] : '',
                    DeliveryReturnDate: estimateData.DeliveryReturnDate ? estimateData.DeliveryReturnDate.split('T')[0] : '',
                    Amount: estimateData.Amount || '0',
                    DiscountAmount: estimateData.DiscountAmount || '0',
                    CGSTAmount: estimateData.CGSTAmount || '0',
                    SGSTAmount: estimateData.SGSTAmount || '0',
                    IGSTAmount: estimateData.IGSTAmount || '0',
                    TransportCharge: estimateData.TransportCharge || '0',
                    RoundOff: estimateData.RoundOff || '0',
                    TotalAmount: estimateData.TotalAmount || '0',
                    AdvanceAmount: estimateData.AdvanceAmount || '0',
                    BalanceAmount: estimateData.BalanceAmount || '0',
                    PaymentMode: estimateData.PaymentMode || 1,
                    Remarks: estimateData.Remarks || '',
                    FinYear: estimateData.FinYear || new Date().getFullYear().toString(),
                    items: (estimateData.EstimateDetails || estimateData.items || []).map(item => ({
                        ItemId: item.ItemId || '',
                        ItemName: item.ItemName || '',
                        EventDays: item.EventDays?.toString() || '1',
                        RentDays: item.RentDays?.toString() || '1',
                        Quantity: item.Quantity?.toString() || '1',
                        Rate: item.Rate?.toString() || '',
                        DiscountAmount: item.DiscountAmount?.toString() || '0',
                        HSNCode: item.HSNCode || '',
                        GSTRate: item.GSTRate?.toString() || '18',
                        TaxableAmount: item.TaxableAmount?.toString() || '',
                        CGSTAmount: item.CGSTAmount?.toString() || '',
                        SGSTAmount: item.SGSTAmount?.toString() || '',
                        TotalAmount: item.TotalAmount?.toString() || ''
                    }))
                });

                // If coming from enquiry (has EnquiryId but no EstimateNo), fetch new bill number
                if (estimateData.EnquiryId && !estimateData.EstimateNo) {
                    setShowNewCustomerForm(true);
                    fetchBillNumberForEstimate();
                }
            } else {
                const today = new Date().toISOString().split('T')[0];
                setFormData({
                    EstimateNo: '',
                    EstimateNumber: '',
                    BillNumber: '',
                    BillPrefix: '',
                    CustomerId: '',
                    CustomerName: '',
                    CustomerTypeId: 1,
                    EnquiryId: '',
                    EnquiryNo: '',
                    EnquiryDate: '',
                    ContactNumber1: '',
                    ContactNumber2: '',
                    GSTNumber: '',
                    AddressLine1: '',
                    AddressLine2: '',
                    Location: '',
                    PinCode: '',
                    BillDate: today,
                    EventName: '',
                    EventLocation: '',
                    EventAddress: '',
                    EventDays: '',
                    ExpectedTime: '',
                    ProgramDate: '',
                    DeliveryDate: '',
                    DeliveryReturnDate: '',
                    Amount: '0',
                    DiscountAmount: '0',
                    CGSTAmount: '0',
                    SGSTAmount: '0',
                    IGSTAmount: '0',
                    TransportCharge: '0',
                    RoundOff: '0',
                    TotalAmount: '0',
                    AdvanceAmount: '0',
                    BalanceAmount: '0',
                    PaymentMode: 1,
                    Remarks: '',
                    RentDays:'',
                    FinYear: new Date().getFullYear().toString(),
                    items: []
                });

                // Fetch bill number for completely new estimate
                fetchBillNumberForEstimate();
            }
            setErrors({});
            setShowSuccessMessage(false);
            setSuccessMessage('');
            setShowSuccessDialog(false);
            setIsBlacklisted(false);

            // Fetch branch data for IsGST setting
            fetchBranchData();
        }
    }, [open, estimateData, mode]);

    // Auto-update all items' EventDays when main EventDays field changes
    useEffect(() => {
        if (formData.EventDays && formData.items.length > 0) {
            const needsUpdate = formData.items.some(item => item.EventDays !== formData.EventDays);

            if (needsUpdate) {
                setFormData(prev => {
                    const updatedItems = prev.items.map(item => ({
                        ...item,
                        EventDays: prev.EventDays
                    }));
                    return {
                        ...prev,
                        items: updatedItems
                    };
                });

                // Recalculate all items after updating EventDays
                setTimeout(() => recalculateTotals(), 0);
            }
        }
    }, [formData.EventDays]);

    // Auto-calculate DeliveryDate and DeliveryReturnDate when ProgramDate or EventDays changes
    // Only in create mode, not when editing/viewing existing data
    useEffect(() => {
        if (mode === 'create' && formData.ProgramDate && formData.EventDays) {
            const programDate = new Date(formData.ProgramDate);
            const eventDays = parseFloat(formData.EventDays) || 0;

            // DeliveryDate = ProgramDate - 1 day
            const deliveryDate = new Date(programDate);
            deliveryDate.setDate(deliveryDate.getDate() - 1);
            const deliveryDateStr = deliveryDate.toISOString().split('T')[0];

            // DeliveryReturnDate = ProgramDate + EventDays + 1 day
            const returnDate = new Date(programDate);
            returnDate.setDate(returnDate.getDate() + eventDays + 1);
            const returnDateStr = returnDate.toISOString().split('T')[0];

            // Only update if different to prevent infinite loop
            if (deliveryDateStr !== formData.DeliveryDate || returnDateStr !== formData.DeliveryReturnDate) {
                setFormData(prev => ({
                    ...prev,
                    DeliveryDate: deliveryDateStr,
                    DeliveryReturnDate: returnDateStr
                }));
            }
        }
    }, [formData.ProgramDate, formData.EventDays, mode]);

    // Auto-calculate ReturnDate when DeliveryDate or EventDays changes
    // Only in create mode, not when editing/viewing existing data
    useEffect(() => {
        if (mode === 'create' && formData.DeliveryDate && formData.EventDays) {
            const deliveryDate = new Date(formData.DeliveryDate);
            const eventDays = parseFloat(formData.EventDays) || 0;

            // ReturnDate = DeliveryDate + EventDays + 1
            const returnDate = new Date(deliveryDate);
            returnDate.setDate(returnDate.getDate() + eventDays + 1);

            const returnDateStr = returnDate.toISOString().split('T')[0];

            // Only update if different to prevent infinite loop
            if (returnDateStr !== formData.DeliveryReturnDate) {
                setFormData(prev => ({
                    ...prev,
                    DeliveryReturnDate: returnDateStr
                }));
            }
        }
    }, [formData.DeliveryDate, formData.EventDays, mode]);

    // Auto-calculate RentDays when DeliveryDate or DeliveryReturnDate changes
    useEffect(() => {
        if (formData.DeliveryDate && formData.DeliveryReturnDate) {
            const deliveryDate = new Date(formData.DeliveryDate);
            const returnDate = new Date(formData.DeliveryReturnDate);

            // Calculate difference in days
            const diffTime = Math.abs(returnDate - deliveryDate);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            // Update RentDays in formData and all items
            setFormData(prev => {
                const updatedItems = prev.items.map(item => ({
                    ...item,
                    RentDays: diffDays
                }));

                return {
                    ...prev,
                    RentDays: diffDays,
                    items: updatedItems
                };
            });

            // Recalculate all item amounts after updating RentDays
            setTimeout(() => recalculateTotals(), 0);
        }
    }, [formData.DeliveryDate, formData.DeliveryReturnDate]);

    // Recalculate all items when isGST changes
    useEffect(() => {
        if (formData.items.length > 0) {
            setTimeout(() => recalculateTotals(), 0);
        }
    }, [isGST]);

    const handleInputChange = (field, value, itemIndex = null) => {
        if (itemIndex !== null) {
            setFormData(prev => {
                const newItems = [...prev.items];
                newItems[itemIndex] = {
                    ...newItems[itemIndex],
                    [field]: value
                };
                return {
                    ...prev,
                    items: newItems
                };
            });

            if (field === 'ItemId') {
                const selectedItem = items.find(item => item.Id === value);
                if (selectedItem) {
                    setFormData(prev => {
                        const newItems = [...prev.items];
                        newItems[itemIndex] = {
                            ...newItems[itemIndex],
                            ItemName: selectedItem.Name || '',
                            HSNCode: selectedItem.HSNCode || '',
                            Rate: selectedItem.EventSaleRate || selectedItem.Rate || '',
                            GSTRate: selectedItem.GSTRate || '18',
                            EventDays: '1',
                            RentDays: '1',
                            Quantity: '1'
                        };
                        return {
                            ...prev,
                            items: newItems
                        };
                    });

                    setTimeout(() => {
                        calculateItemAmounts(itemIndex);
                    }, 0);
                }
            }

            if (['Quantity', 'Rate', 'EventDays', 'RentDays', 'DiscountAmount'].includes(field)) {
                calculateItemAmounts(itemIndex);
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [field]: value
            }));

            if (errors[field]) {
                setErrors(prev => ({
                    ...prev,
                    [field]: ''
                }));
            }

            if (field === 'CustomerId') {
                const selectedCustomer = customers.find(customer => customer.Id === value);
                if (selectedCustomer) {
                    // Check if customer is blacklisted
                    setIsBlacklisted(selectedCustomer.Blacklist === true || selectedCustomer.Blacklist === 1);

                    setFormData(prev => ({
                        ...prev,
                        CustomerName: selectedCustomer.Name || '',
                        CustomerTypeId: selectedCustomer.CustomerTypeId || 1,
                        ContactNumber1: selectedCustomer.ContactNumber1 || selectedCustomer.ContactNumber || '',
                        ContactNumber2: selectedCustomer.ContactNumber2 || '',
                        GSTNumber: selectedCustomer.GSTNumber || '',
                        AddressLine1: selectedCustomer.AddressLine1 || selectedCustomer.Address || '',
                        AddressLine2: selectedCustomer.AddressLine2 || '',
                        Location: selectedCustomer.Location || '',
                        PinCode: selectedCustomer.PinCode || ''
                    }));
                    // Update item rates based on customer type
                    updateItemRatesForCustomerType(selectedCustomer.CustomerTypeId || 1);
                } else {
                    setIsBlacklisted(false);
                }
            }

            if (field === 'CustomerTypeId') {
                // Update item rates when customer type changes
                updateItemRatesForCustomerType(value);
            }

            if (field === 'TransportCharge' || field === 'RoundOff' || field === 'AdvanceAmount') {
                recalculateTotals();
            }
        }
    };

    const calculateItemAmounts = (itemIndex) => {
        setFormData(prev => {
            const newItems = [...prev.items];
            const item = newItems[itemIndex];

            const quantity = parseFloat(item.Quantity) || 0;
            const rate = parseFloat(item.Rate) || 0;
            const rentDays = parseFloat(item.RentDays) || 0;
            let discountAmount = parseFloat(item.DiscountAmount) || 0;
            const gstRate = parseFloat(item.GSTRate) || 0;

            // Amount = RentDays * Qty * Rate
            const amount = rentDays * quantity * rate;

            // Validate: Discount amount should not be greater than amount
            if (discountAmount > amount) {
                discountAmount = amount;
            }

            const taxableAmount = amount - discountAmount;

            // Calculate GST based on current isGST flag - use functional update to get latest value
            const cgstAmount = isGST ? (taxableAmount * gstRate) / 200 : 0;
            const sgstAmount = isGST ? (taxableAmount * gstRate) / 200 : 0;

            const itemTotalAmount = taxableAmount + cgstAmount + sgstAmount;

            newItems[itemIndex] = {
                ...item,
                DiscountAmount: discountAmount,
                TaxableAmount: taxableAmount.toFixed(2),
                CGSTAmount: cgstAmount.toFixed(2),
                SGSTAmount: sgstAmount.toFixed(2),
                TotalAmount: itemTotalAmount.toFixed(2)
            };

            // Calculate overall totals
            const overallAmount = newItems.reduce((sum, item) => {
                const qty = parseFloat(item.Quantity) || 0;
                const rt = parseFloat(item.Rate) || 0;
                const rd = parseFloat(item.RentDays) || 0;
                return sum + (rd * qty * rt);
            }, 0);

            const overallDiscountAmount = newItems.reduce((sum, item) => {
                return sum + parseFloat(item.DiscountAmount || 0);
            }, 0);

            const overallCGST = newItems.reduce((sum, item) => sum + parseFloat(item.CGSTAmount || 0), 0);
            const overallSGST = newItems.reduce((sum, item) => sum + parseFloat(item.SGSTAmount || 0), 0);
            const overallIGST = 0;

            const transportCharge = parseFloat(prev.TransportCharge) || 0;
            const roundOff = parseFloat(prev.RoundOff) || 0;
            const totalAmount = overallAmount - overallDiscountAmount + overallCGST + overallSGST + overallIGST + transportCharge + roundOff;
            const advanceAmount = parseFloat(prev.AdvanceAmount) || 0;
            const balanceAmount = totalAmount - advanceAmount;

            return {
                ...prev,
                items: newItems,
                Amount: overallAmount.toFixed(2),
                DiscountAmount: overallDiscountAmount.toFixed(2),
                CGSTAmount: overallCGST.toFixed(2),
                SGSTAmount: overallSGST.toFixed(2),
                IGSTAmount: overallIGST.toFixed(2),
                TotalAmount: totalAmount.toFixed(2),
                BalanceAmount: balanceAmount.toFixed(2)
            };
        });
    };

    const recalculateTotals = () => {
        setFormData(prev => {
            const overallAmount = prev.items.reduce((sum, item) => {
                const qty = parseFloat(item.Quantity) || 0;
                const rt = parseFloat(item.Rate) || 0;
                const rd = parseFloat(item.RentDays) || 0;
                return sum + (rd * qty * rt);
            }, 0);

            const overallDiscountAmount = prev.items.reduce((sum, item) => {
                return sum + parseFloat(item.DiscountAmount || 0);
            }, 0);

            const overallCGST = prev.items.reduce((sum, item) => sum + parseFloat(item.CGSTAmount || 0), 0);
            const overallSGST = prev.items.reduce((sum, item) => sum + parseFloat(item.SGSTAmount || 0), 0);
            const overallIGST = 0;

            const transportCharge = parseFloat(prev.TransportCharge) || 0;
            const roundOff = parseFloat(prev.RoundOff) || 0;
            const totalAmount = overallAmount - overallDiscountAmount + overallCGST + overallSGST + overallIGST + transportCharge + roundOff;
            const advanceAmount = parseFloat(prev.AdvanceAmount) || 0;
            const balanceAmount = totalAmount - advanceAmount;

            return {
                ...prev,
                Amount: overallAmount.toFixed(2),
                DiscountAmount: overallDiscountAmount.toFixed(2),
                CGSTAmount: overallCGST.toFixed(2),
                SGSTAmount: overallSGST.toFixed(2),
                IGSTAmount: overallIGST.toFixed(2),
                TotalAmount: totalAmount.toFixed(2),
                BalanceAmount: balanceAmount.toFixed(2)
            };
        });
    };

    const updateItemRatesForCustomerType = (customerTypeId) => {
        setFormData(prev => {
            const newItems = prev.items.map(item => {
                if (item.ItemId) {
                    const selectedItem = items.find(i => i.Id === item.ItemId);
                    if (selectedItem) {
                        const rate = customerTypeId === 1 ? selectedItem.EventSaleRate : selectedItem.OthersSaleRate;
                        return {
                            ...item,
                            Rate: rate || selectedItem.Rate || ''
                        };
                    }
                }
                return item;
            });
            return {
                ...prev,
                items: newItems
            };
        });
        // Recalculate all item amounts after rate update
        setTimeout(() => recalculateTotals(), 0);
    };

    const addItem = () => {
        handleOpenGetItemsModal();
    };

    const removeItem = (itemIndex) => {
        setFormData(prev => {
            const newItems = prev.items.filter((_, index) => index !== itemIndex);
            return {
                ...prev,
                items: newItems
            };
        });
        setTimeout(() => recalculateTotals(), 0);
    };

    const validateForm = () => {
        const newErrors = {};

        // Customer validation
        if (!formData.CustomerId && !showNewCustomerForm) newErrors.CustomerId = 'Customer is required';
        if (showNewCustomerForm && !formData.CustomerName) newErrors.CustomerName = 'Customer name is required';
        if (!formData.BillDate) newErrors.BillDate = 'Bill date is required';

        if (formData.items.length === 0) {
            newErrors.items = 'At least one item is required';
        }

        formData.items.forEach((item, index) => {
            if (!item.Quantity || item.Quantity <= 0) {
                newErrors[`Quantity_${index}`] = 'Valid quantity is required';
            }
            if (!item.Rate || item.Rate <= 0) {
                newErrors[`Rate_${index}`] = 'Valid rate is required';
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        setLoading(true);
        try {
            const estimateMainData = {
                EstimateNo: formData.EstimateNo,
                EstimateNumber: formData.BillNumber,
                BillNumber: formData.BillNumber,
                BillPrefix: formData.BillPrefix,
                CustomerId: formData.CustomerId || null,
                CustomerName: formData.CustomerName,
                CustomerTypeId: formData.CustomerTypeId || 1,
                EnquiryId: formData.EnquiryId || null,
                EnquiryNo: formData.EnquiryNo || '',
                ContactNumber1: formData.ContactNumber1,
                ContactNumber2: formData.ContactNumber2,
                GSTNumber: formData.GSTNumber,
                AddressLine1: formData.AddressLine1,
                AddressLine2: formData.AddressLine2,
                Location: formData.Location,
                PinCode: formData.PinCode,
                BillDate: formData.BillDate,
                EventName: formData.EventName,
                EventLocation: formData.EventLocation,
                EventAddress: formData.EventAddress,
                EventDays: parseFloat(formData.EventDays) || 0,
                RentDays: parseFloat(formData.RentDays) || 0,
                ExpectedTime: formData.ExpectedTime || '',
                ProgramDate: formData.ProgramDate || null,
                DeliveryDate: formData.DeliveryDate || null,
                DeliveryReturnDate: formData.DeliveryReturnDate || null,
                Amount: parseFloat(formData.Amount) || 0,
                DiscountAmount: parseFloat(formData.DiscountAmount) || 0,
                CGSTAmount: parseFloat(formData.CGSTAmount) || 0,
                SGSTAmount: parseFloat(formData.SGSTAmount) || 0,
                IGSTAmount: parseFloat(formData.IGSTAmount) || 0,
                TransportCharge: parseFloat(formData.TransportCharge) || 0,
                RoundOff: parseFloat(formData.RoundOff) || 0,
                TotalAmount: parseFloat(formData.TotalAmount) || 0,
                AdvanceAmount: parseFloat(formData.AdvanceAmount) || 0,
                BalanceAmount: parseFloat(formData.BalanceAmount) || 0,
                PaymentMode: parseInt(formData.PaymentMode) || 1,
                Remarks: formData.Remarks,
                FinYear: formData.FinYear,
                CompanyId: companyId,
                BranchId: branchId,
                CreatedBy: userId,
                ModifiedBy: userId,
                IsActive: true
            };

            const estimateDetails = formData.items.map(item => ({
                ItemId: item.ItemId,
                ItemName: item.ItemName,
                // EventDays: parseFloat(item.EventDays) || 0,
                // RentDays: parseFloat(item.RentDays) || 0,
                Quantity: parseFloat(item.Quantity) || 0,
                Rate: parseFloat(item.Rate) || 0,
                DiscountAmount: parseFloat(item.DiscountAmount) || 0,
                HSNCode: item.HSNCode,
                GSTRate: parseFloat(item.GSTRate) || 0,
                TaxableAmount: parseFloat(item.TaxableAmount) || 0,
                CGSTAmount: parseFloat(item.CGSTAmount) || 0,
                SGSTAmount: parseFloat(item.SGSTAmount) || 0,
                TotalAmount: parseFloat(item.TotalAmount) || 0,
                CompanyId: companyId,
                BranchId: branchId,
                IsActive: true
            }));

            const dataToSend = {
                Estimate: estimateMainData,
                EstimateDetails: estimateDetails
            };

            let response;
            if (mode === 'edit' && formData.Id) {
                dataToSend.Estimate.Id = formData.Id;
                response = await updateData('EstimateApi', formData.Id, dataToSend, token);
            } else {
                response = await postData('EstimateApi', dataToSend, token);
            }

            const isSuccess = response && (
                response.responseCode === '1111' ||
                response.responseCode === 1111 ||
                response.success === true ||
                response.status === 200 ||
                response.status === 201 ||
                (response.data && response.data.success) ||
                !response.error
            );

            if (isSuccess) {
                onClose();
                const message = mode === 'edit' ? 'Estimate updated successfully!' : 'Estimate created successfully!';
                setSuccessMessage(message);
                setShowSuccessDialog(true);

                setTimeout(() => {
                    setShowSuccessDialog(false);
                    onSave();
                }, 3000);
            } else {
                setSuccessMessage('Failed to save estimate. Please try again.');
                setShowSuccessMessage(true);
            }
        } catch (error) {
            console.error('Error saving estimate:', error);
            setSuccessMessage('Error occurred while saving estimate. Please try again.');
            setShowSuccessMessage(true);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateNewCustomer = () => {
        setShowNewCustomerForm(true);
        setIsBlacklisted(false);
        setFormData(prev => ({
            ...prev,
            CustomerId: '',
            CustomerName: '',
            ContactNumber1: '',
            ContactNumber2: '',
            GSTNumber: '',
            AddressLine1: '',
            AddressLine2: '',
            Location: '',
            PinCode: ''
        }));
    };

    // Get Items modal handlers
    const handleOpenGetItemsModal = async () => {
        setGetItemsModalOpen(true);
        setGetItemsLoading(true);
        setSelectedItems([]);
        setGetItemsSearchValue('');
        setCurrentItemsPage(1);

        const loadingTimeout = setTimeout(() => {
            setGetItemsLoading(false);
        }, 3000);

        try {
            setGetItemsLoading(false);
        } finally {
            clearTimeout(loadingTimeout);
            setGetItemsLoading(false);
        }
    };

    const handleCloseGetItemsModal = () => {
        setGetItemsModalOpen(false);
        setSelectedItems([]);
        setGetItemsSearchValue('');
    };

    const handleItemSelection = (item, isSelected) => {
        if (isSelected) {
            setSelectedItems(prev => [...prev, {
                ...item,
                selectedQuantity: 1,
                selectedEventDays: 1,
                selectedRentDays: 1,
                selectedRate: item.EventSaleRate || item.Rate || 0,
                selectedDiscountAmount: 0
            }]);
        } else {
            setSelectedItems(prev => prev.filter(si => si.Id !== item.Id));
        }
    };

    const handleGetItemsSearch = (searchValue) => {
        setGetItemsSearchValue(searchValue);
        setCurrentItemsPage(1);
    };

    const getFilteredItems = () => {
        if (!getItemsSearchValue.trim()) {
            return items;
        }

        const searchTerm = getItemsSearchValue.toLowerCase().trim();
        return items.filter(item =>
            item.Name?.toLowerCase().includes(searchTerm) ||
            item.Code?.toLowerCase().includes(searchTerm) ||
            item.HSNCode?.toLowerCase().includes(searchTerm)
        );
    };

    const getPaginatedItems = () => {
        const filtered = getFilteredItems();
        const startIndex = (currentItemsPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filtered.slice(startIndex, endIndex);
    };

    const getTotalPages = Math.ceil(getFilteredItems().length / itemsPerPage);

    const handleSelectedItemChange = (itemId, field, value) => {
        setSelectedItems(prev => prev.map(item =>
            item.Id === itemId ? { ...item, [field]: value } : item
        ));
    };

    const isItemAlreadyInEstimate = (itemId) => {
        return formData.items.some(item => item.ItemId === itemId);
    };

    const handleAddSelectedItems = async () => {
        if (selectedItems.length === 0) {
            handleCloseGetItemsModal();
            return;
        }

        setAddingItemsLoading(true);

        try {
            const itemsToAdd = selectedItems.filter(item => !isItemAlreadyInEstimate(item.Id));

            if (itemsToAdd.length === 0) {
                handleCloseGetItemsModal();
                return;
            }

            const newItems = itemsToAdd.map(item => {
                const quantity = parseFloat(item.selectedQuantity) || 1;
                const rate = parseFloat(item.selectedRate) || 0;
                const rentDays = parseFloat(formData.RentDays) || 1; // Use formData.RentDays instead of item.selectedRentDays
                let discountAmount = parseFloat(item.selectedDiscountAmount) || 0;
                const gstRate = parseFloat(item.GSTRate) || 18;

                const amount = rentDays * quantity * rate;

                // Validate: Discount amount should not be greater than amount
                if (discountAmount > amount) {
                    discountAmount = amount;
                }

                const taxableAmount = amount - discountAmount;
                const cgstAmount = isGST ? (taxableAmount * gstRate) / 200 : 0;
                const sgstAmount = isGST ? (taxableAmount * gstRate) / 200 : 0;
                const totalAmount = taxableAmount + cgstAmount + sgstAmount;

                return {
                    ItemId: item.Id,
                    ItemName: item.Name,
                    RentDays: rentDays.toString(),
                    Quantity: quantity.toString(),
                    Rate: rate.toString(),
                    DiscountAmount: discountAmount,
                    HSNCode: item.HSNCode || '',
                    GSTRate: gstRate.toString(),
                    TaxableAmount: taxableAmount.toFixed(2),
                    CGSTAmount: cgstAmount.toFixed(2),
                    SGSTAmount: sgstAmount.toFixed(2),
                    TotalAmount: totalAmount.toFixed(2)
                };
            });

            setFormData(prev => ({
                ...prev,
                items: [...prev.items, ...newItems]
            }));

            setTimeout(() => recalculateTotals(), 0);
            handleCloseGetItemsModal();
        } catch (error) {
            console.error('Error adding selected items:', error);
        } finally {
            setAddingItemsLoading(false);
        }
    };

    const renderField = (label, value, fieldProps = {}) => {
        return (
            <TextField
                fullWidth
                label={label}
                value={value}
                size="small"
                InputProps={{ readOnly: mode === 'view' }}
                sx={{
                    '& .MuiInputLabel-root': { fontSize: '0.75rem', top: '-6px' },
                    '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 },
                    '& .MuiInputBase-root': { height: '28px' }
                }}
                {...fieldProps}
            />
        );
    };

    if (!open) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: { xs: '0px', sm: '16px' },
                    maxHeight: { xs: '100vh', sm: '96vh' },
                    width: { xs: '100vw', sm: '95vw' },
                    maxWidth: { xs: '100vw', sm: '1400px' },
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
                    m: { xs: 0, sm: 2 }
                }
            }}
        >
            <DialogTitle sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                p: { xs: 1, sm: 0.75 },
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)',
                    pointerEvents: 'none'
                }
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 0.75 } }}>
                        <Avatar sx={{
                            bgcolor: 'rgba(255,255,255,0.25)',
                            width: { xs: 32, sm: 28 },
                            height: { xs: 32, sm: 28 },
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                        }}>
                            <PriceIcon sx={{ fontSize: { xs: 18, sm: 16 }, color: 'white' }} />
                        </Avatar>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '0.9rem' }, mb: 0, letterSpacing: '-0.5px' }}>
                                {mode === 'view' ? 'View Estimate' : mode === 'edit' ? 'Edit Estimate' : 'Create New Estimate'}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            color: 'white',
                            bgcolor: 'rgba(255,255,255,0.15)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                            width: { xs: 32, sm: 28 },
                            height: { xs: 32, sm: 28 }
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: { xs: 0.5, sm: 1 }, bgcolor: '#f8fafc' }}>
                {loading && <LinearProgress sx={{ mb: 0.75, borderRadius: 1 }} />}

                <Grid container spacing={{ xs: 0.5, sm: 0.75 }}>
                    {/* Estimate Details Section */}
                    <Grid item xs={12}>
                        <Paper elevation={0} sx={{
                            p: 1,
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            bgcolor: 'white',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                            }
                        }}>
                            <Grid container spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                                {/* Estimate Details Caption with Icon */}
                                <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: '6px',
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                                    }}>
                                        <DescriptionIcon sx={{ color: 'white', fontSize: 12 }} />
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a202c', fontSize: '0.8rem', letterSpacing: '-0.3px' }}>
                                        Estimate Details
                                    </Typography>
                                </Grid>

                                {/* Right side container for Estimate Number, Date, Enquiry Number, Enquiry Date, and Payment Mode */}
                                <Grid item xs={12} md={9} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                    {/* Enquiry Number - Show first if exists */}
                                    {formData.EnquiryNo && (
                                        <TextField
                                            size="small"
                                            label="Enquiry Number"
                                            value={formData.EnquiryNo || ''}
                                            InputProps={{
                                                readOnly: true,
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon sx={{ color: '#a0aec0', fontSize: 16 }} />
                                                    </InputAdornment>
                                                )
                                            }}
                                            sx={{
                                                width: '180px',
                                                '& .MuiInputBase-root': {
                                                    height: '28px',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 600,
                                                    borderRadius: '6px',
                                                    bgcolor: '#f7fafc'
                                                },
                                                '& .MuiInputLabel-root': { fontSize: '0.75rem', fontWeight: 600, color: '#000' },
                                                '& .MuiInputBase-input': { color: '#000' }
                                            }}
                                        />
                                    )}

                                    {/* Enquiry Date - Show second if exists */}
                                    {formData.EnquiryDate && (
                                        <TextField
                                            size="small"
                                            label="Enquiry Date"
                                            type="date"
                                            value={formData.EnquiryDate ? formData.EnquiryDate.split('T')[0] : ''}
                                            InputProps={{
                                                readOnly: true,
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <EventIcon sx={{ color: '#a0aec0', fontSize: 16 }} />
                                                    </InputAdornment>
                                                )
                                            }}
                                            InputLabelProps={{ shrink: true }}
                                            sx={{
                                                width: '180px',
                                                '& .MuiInputBase-root': {
                                                    height: '28px',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 600,
                                                    borderRadius: '6px',
                                                    bgcolor: '#f7fafc'
                                                },
                                                '& .MuiInputLabel-root': { fontSize: '0.75rem', fontWeight: 600, color: '#000' },
                                                '& .MuiInputBase-input': { color: '#000' }
                                            }}
                                        />
                                    )}

                                    {/* Estimate Number */}
                                    <TextField
                                        size="small"
                                        label="Estimate Number"
                                        value={formData.EstimateNo || ''}
                                        InputProps={{
                                            readOnly: true,
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <DescriptionIcon sx={{ color: '#a0aec0', fontSize: 16 }} />
                                                </InputAdornment>
                                            )
                                        }}
                                        sx={{
                                            width: '180px',
                                            '& .MuiInputBase-root': {
                                                height: '28px',
                                                fontSize: '0.75rem',
                                                fontWeight: 700,
                                                borderRadius: '6px'
                                            },
                                            '& .MuiInputLabel-root': { fontSize: '0.75rem', fontWeight: 700, color: '#000' },
                                            '& .MuiInputBase-input': { color: '#000' },
                                            '& .MuiFormHelperText-root': { fontSize: '0.65rem', mt: 0.25 },
                                            '& .MuiOutlinedInput-root': {
                                                '&:hover fieldset': {
                                                    borderColor: '#667eea',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#667eea',
                                                    borderWidth: '2px'
                                                }
                                            }
                                        }}
                                    />

                                    {/* Estimate Date */}
                                    <TextField
                                        size="small"
                                        label="Estimate Date *"
                                        type="date"
                                        value={formData.BillDate || ''}
                                        onChange={(e) => handleInputChange('BillDate', e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        error={!!errors.BillDate}
                                        helperText={errors.BillDate}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <EventIcon sx={{ color: '#a0aec0', fontSize: 16 }} />
                                                </InputAdornment>
                                            )
                                        }}
                                        sx={{
                                            width: '180px',
                                            '& .MuiInputBase-root': {
                                                height: '28px',
                                                fontSize: '0.75rem',
                                                fontWeight: 700,
                                                borderRadius: '6px'
                                            },
                                            '& .MuiInputLabel-root': { fontSize: '0.75rem', fontWeight: 700, color: '#000' },
                                            '& .MuiInputBase-input': { color: '#000' },
                                            '& .MuiFormHelperText-root': { fontSize: '0.65rem', mt: 0.25 },
                                            '& .MuiOutlinedInput-root': {
                                                '&:hover fieldset': {
                                                    borderColor: '#667eea',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#667eea',
                                                    borderWidth: '2px'
                                                }
                                            }
                                        }}
                                    />

                                    {/* Payment Mode */}
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        bgcolor: 'linear-gradient(135deg, #f0f4ff 0%, #e8eeff 100%)',
                                        borderRadius: '6px',
                                        p: 0.5,
                                        border: '1px solid #e0e7ff'
                                    }}>
                                        <Box sx={{
                                            width: 20,
                                            height: 20,
                                            borderRadius: '4px',
                                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                                        }}>
                                            <PriceIcon sx={{ color: 'white', fontSize: 14 }} />
                                        </Box>
                                        <FormControl size="small" sx={{ minWidth: 150 }}>
                                            <InputLabel sx={{
                                                fontSize: '0.75rem',
                                                fontWeight: 700,
                                                color: '#4c1d95',
                                                '&.Mui-focused': { color: '#5b21b6' }
                                            }}>
                                                Payment Mode
                                            </InputLabel>
                                            <Select
                                                value={formData.PaymentMode}
                                                label="Payment Mode"
                                                onChange={(e) => handleInputChange('PaymentMode', e.target.value)}
                                                inputProps={{ readOnly: mode === 'view' }}
                                                sx={{
                                                    fontSize: '0.75rem',
                                                    height: '28px',
                                                    borderRadius: '6px',
                                                    bgcolor: 'white',
                                                    fontWeight: 700,
                                                    color: '#1e293b',
                                                    '& .MuiSelect-select': {
                                                        py: 0.5,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5
                                                    },
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: '#c7d2fe',
                                                        borderWidth: '2px'
                                                    },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: '#a78bfa',
                                                    },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: '#8b5cf6',
                                                        borderWidth: '2px'
                                                    }
                                                }}
                                                MenuProps={{
                                                    PaperProps: {
                                                        sx: {
                                                            borderRadius: '8px',
                                                            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                                                            mt: 0.5,
                                                            '& .MuiMenuItem-root': {
                                                                fontSize: '0.75rem',
                                                                minHeight: '32px',
                                                                py: 0.75,
                                                                px: 2,
                                                                fontWeight: 500,
                                                                '&:hover': {
                                                                    bgcolor: '#f3f4f6',
                                                                    color: '#5b21b6'
                                                                },
                                                                '&.Mui-selected': {
                                                                    bgcolor: '#ede9fe',
                                                                    color: '#5b21b6',
                                                                    fontWeight: 600,
                                                                    '&:hover': {
                                                                        bgcolor: '#ddd6fe'
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    }
                                                }}
                                            >
                                                <MenuItem value={1}>💵 Cash</MenuItem>
                                                <MenuItem value={2}>💳 Card</MenuItem>
                                                <MenuItem value={3}>📱 UPI</MenuItem>
                                                <MenuItem value={4}>🏦 Bank Transfer</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    {/* Customer Information */}
                    <Grid item xs={12}>
                        {isBlacklisted && (
                            <Alert
                                severity="error"
                                icon={<WarningIcon />}
                                sx={{
                                    mb: 1,
                                    borderRadius: '8px',
                                    fontWeight: 700,
                                    fontSize: '0.85rem',
                                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                    color: 'white',
                                    '& .MuiAlert-icon': {
                                        color: 'white'
                                    }
                                }}
                            >
                                ⚠️ BLACKLISTED CUSTOMER - Proceed with caution!
                            </Alert>
                        )}
                        <Paper elevation={0} sx={{
                            p: 1,
                            border: isBlacklisted ? '2px solid #ef4444' : '1px solid #e2e8f0',
                            borderRadius: '8px',
                            bgcolor: isBlacklisted ? '#fef2f2' : 'white',
                            boxShadow: isBlacklisted ? '0 4px 12px rgba(239, 68, 68, 0.2)' : '0 1px 3px rgba(0,0,0,0.05)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                boxShadow: isBlacklisted ? '0 6px 16px rgba(239, 68, 68, 0.3)' : '0 4px 12px rgba(0,0,0,0.08)'
                            }
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{
                                        width: 20, height: 20, borderRadius: '6px',
                                        background: isBlacklisted
                                            ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                                            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: isBlacklisted
                                            ? '0 4px 12px rgba(239, 68, 68, 0.4)'
                                            : '0 4px 12px rgba(102, 126, 234, 0.3)'
                                    }}>
                                        {isBlacklisted ? (
                                            <WarningIcon sx={{ color: 'white', fontSize: 12 }} />
                                        ) : (
                                            <PersonIcon sx={{ color: 'white', fontSize: 12 }} />
                                        )}
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" sx={{
                                            fontWeight: 700,
                                            color: isBlacklisted ? '#dc2626' : '#1a202c',
                                            fontSize: '0.8rem',
                                            letterSpacing: '-0.3px'
                                        }}>
                                            Customer Information {isBlacklisted && '⚠️'}
                                        </Typography>
                                    </Box>
                                </Box>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={isGST}
                                            onChange={(e) => setIsGST(e.target.checked)}
                                            disabled={mode === 'view'}
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                    color: isBlacklisted ? '#ef4444' : '#667eea',
                                                },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                    backgroundColor: isBlacklisted ? '#ef4444' : '#667eea',
                                                }
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography sx={{
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            color: isGST ? (isBlacklisted ? '#ef4444' : '#667eea') : '#9ca3af'
                                        }}>
                                            GST {isGST ? 'ON' : 'OFF'}
                                        </Typography>
                                    }
                                    sx={{ m: 0 }}
                                />
                            </Box>

                            <Grid container spacing={1.5} rowSpacing={2}>
                                <Grid item xs={12} md={4}>
                                    {!showNewCustomerForm ? (
                                        <Box>
                                            <Autocomplete
                                                options={customers}
                                                getOptionLabel={(option) => {
                                                    if (typeof option === 'string') return option;
                                                    const phone = option.ContactNumber1 || option.ContactNumber || '';
                                                    return `${option.Name} - ${phone}`;
                                                }}
                                                value={customers.find(c => c.Id === formData.CustomerId) || null}
                                                onChange={(event, newValue) => {
                                                    if (newValue) {
                                                        handleInputChange('CustomerId', newValue.Id);
                                                    } else {
                                                        handleInputChange('CustomerId', '');
                                                    }
                                                }}
                                                disabled={mode === 'view'}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Search Customer by Name or Phone *"
                                                        error={!!errors.CustomerId}
                                                        helperText={errors.CustomerId}
                                                        size="small"
                                                        InputProps={{
                                                            ...params.InputProps,
                                                            startAdornment: (
                                                                <>
                                                                    <InputAdornment position="start">
                                                                        <SearchIcon sx={{ color: '#a0aec0', fontSize: 16 }} />
                                                                    </InputAdornment>
                                                                    {params.InputProps.startAdornment}
                                                                </>
                                                            )
                                                        }}
                                                        sx={{
                                                            '& .MuiInputLabel-root': {
                                                                fontSize: '0.75rem',
                                                                color: isBlacklisted ? '#ef4444' : undefined
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                fontSize: '0.75rem',
                                                                py: 0,
                                                                color: isBlacklisted ? '#dc2626' : undefined,
                                                                fontWeight: isBlacklisted ? 600 : 400
                                                            },
                                                            '& .MuiInputBase-root': {
                                                                height: '28px',
                                                                borderRadius: '8px',
                                                                bgcolor: isBlacklisted ? '#fef2f2' : undefined
                                                            },
                                                            '& .MuiFormHelperText-root': { fontSize: '0.65rem', mt: 0.25 },
                                                            '& .MuiOutlinedInput-root': {
                                                                '& fieldset': {
                                                                    borderColor: isBlacklisted ? '#ef4444' : undefined,
                                                                    borderWidth: isBlacklisted ? '2px' : '1px'
                                                                },
                                                                '&:hover fieldset': {
                                                                    borderColor: isBlacklisted ? '#dc2626' : '#667eea',
                                                                },
                                                                '&.Mui-focused fieldset': {
                                                                    borderColor: isBlacklisted ? '#dc2626' : '#667eea',
                                                                    borderWidth: '2px'
                                                                }
                                                            }
                                                        }}
                                                    />
                                                )}
                                                componentsProps={{
                                                    paper: {
                                                        sx: {
                                                            fontSize: '0.75rem',
                                                            '& .MuiAutocomplete-option': {
                                                                fontSize: '0.75rem',
                                                                minHeight: '32px',
                                                                padding: '4px 12px'
                                                            },
                                                            '& .MuiAutocomplete-listbox': {
                                                                padding: '4px 0'
                                                            }
                                                        }
                                                    }
                                                }}
                                                size="small"
                                            />
                                            {mode !== 'view' && (
                                                <Button
                                                    variant="text"
                                                    size="small"
                                                    startIcon={<AddIcon />}
                                                    onClick={handleCreateNewCustomer}
                                                    sx={{
                                                        mt: 1,
                                                        textTransform: 'none',
                                                        fontSize: '0.75rem',
                                                        py: 0.5,
                                                        color: '#667eea',
                                                        fontWeight: 600,
                                                        '&:hover': {
                                                            bgcolor: '#f0f4ff'
                                                        }
                                                    }}
                                                >
                                                    Create New Customer
                                                </Button>
                                            )}
                                        </Box>
                                    ) : (
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Customer Name *"
                                            value={formData.CustomerName}
                                            onChange={(e) => handleInputChange('CustomerName', e.target.value)}
                                            InputProps={{
                                                readOnly: mode === 'view',
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <PersonIcon sx={{ color: '#a0aec0', fontSize: 18 }} />
                                                    </InputAdornment>
                                                )
                                            }}
                                            error={!!errors.CustomerName}
                                            helperText={errors.CustomerName}
                                            sx={{
                                                '& .MuiInputBase-root': {
                                                    height: '28px',
                                                    fontSize: '0.75rem',
                                                    bgcolor: mode === 'view' ? '#f7fafc' : 'white',
                                                    borderRadius: '8px'
                                                },
                                                '& .MuiInputLabel-root': { fontSize: '0.75rem' },
                                                '& .MuiFormHelperText-root': { fontSize: '0.75rem', mt: 0.5 },
                                                '& .MuiOutlinedInput-root': {
                                                    '&:hover fieldset': {
                                                        borderColor: '#667eea',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#667eea',
                                                        borderWidth: '2px'
                                                    }
                                                }
                                            }}
                                        />
                                    )}
                                </Grid>

                                <Grid item xs={12} md={2}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel sx={{ fontSize: '0.75rem' }}>Customer Type</InputLabel>
                                        <Select
                                            value={formData.CustomerTypeId || 1}
                                            label="Customer Type"
                                            onChange={(e) => handleInputChange('CustomerTypeId', e.target.value)}
                                            inputProps={{ readOnly: mode === 'view' }}
                                            startAdornment={(
                                                <InputAdornment position="start">
                                                    <BusinessIcon sx={{ color: '#a0aec0', fontSize: 16, ml: 1 }} />
                                                </InputAdornment>
                                            )}
                                            sx={{
                                                fontSize: '0.75rem',
                                                height: '28px',
                                                borderRadius: '6px',
                                                '& .MuiSelect-select': { py: 0.5 },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#667eea',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#667eea',
                                                    borderWidth: '2px'
                                                }
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        '& .MuiMenuItem-root': {
                                                            fontSize: '0.7rem',
                                                            minHeight: '32px',
                                                            py: 0.75
                                                        }
                                                    }
                                                }
                                            }}
                                        >
                                            <MenuItem value={1}>Events</MenuItem>
                                            <MenuItem value={2}>Others</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Contact Number 1"
                                        value={formData.ContactNumber1}
                                        onChange={(e) => handleInputChange('ContactNumber1', e.target.value)}
                                        InputProps={{
                                            readOnly: mode === 'view',
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <PhoneIcon sx={{ color: '#a0aec0', fontSize: 18 }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                height: '28px',
                                                fontSize: '0.75rem',
                                                borderRadius: '8px'
                                            },
                                            '& .MuiInputLabel-root': { fontSize: '0.75rem' },
                                            '& .MuiOutlinedInput-root': {
                                                '&:hover fieldset': {
                                                    borderColor: '#667eea',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#667eea',
                                                    borderWidth: '2px'
                                                }
                                            }
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Contact Number 2"
                                        value={formData.ContactNumber2}
                                        onChange={(e) => handleInputChange('ContactNumber2', e.target.value)}
                                        InputProps={{
                                            readOnly: mode === 'view',
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <PhoneIcon sx={{ color: '#a0aec0', fontSize: 18 }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                height: '28px',
                                                fontSize: '0.75rem',
                                                borderRadius: '8px'
                                            },
                                            '& .MuiInputLabel-root': { fontSize: '0.75rem' },
                                            '& .MuiOutlinedInput-root': {
                                                '&:hover fieldset': {
                                                    borderColor: '#667eea',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#667eea',
                                                    borderWidth: '2px'
                                                }
                                            }
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Location"
                                        value={formData.Location}
                                        onChange={(e) => handleInputChange('Location', e.target.value)}
                                        InputProps={{
                                            readOnly: mode === 'view'
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                height: '28px',
                                                fontSize: '0.75rem',
                                                borderRadius: '8px'
                                            },
                                            '& .MuiInputLabel-root': { fontSize: '0.75rem' },
                                            '& .MuiOutlinedInput-root': {
                                                '&:hover fieldset': {
                                                    borderColor: '#667eea',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#667eea',
                                                    borderWidth: '2px'
                                                }
                                            }
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Address Line 1"
                                        value={formData.AddressLine1}
                                        onChange={(e) => handleInputChange('AddressLine1', e.target.value)}
                                        InputProps={{
                                            readOnly: mode === 'view',
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <LocationIcon sx={{ color: '#a0aec0', fontSize: 18 }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                height: '28px',
                                                fontSize: '0.75rem',
                                                borderRadius: '8px'
                                            },
                                            '& .MuiInputLabel-root': { fontSize: '0.75rem' },
                                            '& .MuiOutlinedInput-root': {
                                                '&:hover fieldset': {
                                                    borderColor: '#667eea',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#667eea',
                                                    borderWidth: '2px'
                                                }
                                            }
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Address Line 2"
                                        value={formData.AddressLine2}
                                        onChange={(e) => handleInputChange('AddressLine2', e.target.value)}
                                        InputProps={{
                                            readOnly: mode === 'view',
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <LocationIcon sx={{ color: '#a0aec0', fontSize: 18 }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                height: '28px',
                                                fontSize: '0.75rem',
                                                borderRadius: '8px'
                                            },
                                            '& .MuiInputLabel-root': { fontSize: '0.75rem' },
                                            '& .MuiOutlinedInput-root': {
                                                '&:hover fieldset': {
                                                    borderColor: '#667eea',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#667eea',
                                                    borderWidth: '2px'
                                                }
                                            }
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Pin Code"
                                        value={formData.PinCode}
                                        onChange={(e) => handleInputChange('PinCode', e.target.value)}
                                        InputProps={{
                                            readOnly: mode === 'view'
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                height: '28px',
                                                fontSize: '0.75rem',
                                                borderRadius: '8px'
                                            },
                                            '& .MuiInputLabel-root': { fontSize: '0.75rem' },
                                            '& .MuiOutlinedInput-root': {
                                                '&:hover fieldset': {
                                                    borderColor: '#667eea',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#667eea',
                                                    borderWidth: '2px'
                                                }
                                            }
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="GST Number"
                                        value={formData.GSTNumber}
                                        onChange={(e) => handleInputChange('GSTNumber', e.target.value)}
                                        InputProps={{
                                            readOnly: mode === 'view'
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                height: '28px',
                                                fontSize: '0.75rem',
                                                borderRadius: '8px'
                                            },
                                            '& .MuiInputLabel-root': { fontSize: '0.75rem' },
                                            '& .MuiOutlinedInput-root': {
                                                '&:hover fieldset': {
                                                    borderColor: '#667eea',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#667eea',
                                                    borderWidth: '2px'
                                                }
                                            }
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    {/* Event Details */}
                    <Grid item xs={12}>
                        <Paper elevation={0} sx={{
                            p: 1,
                            border: isBlacklisted ? '2px solid #ef4444' : '1px solid #e2e8f0',
                            borderRadius: '8px',
                            bgcolor: isBlacklisted ? '#fef2f2' : 'white',
                            boxShadow: isBlacklisted ? '0 4px 12px rgba(239, 68, 68, 0.2)' : '0 1px 3px rgba(0,0,0,0.05)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                boxShadow: isBlacklisted ? '0 6px 16px rgba(239, 68, 68, 0.3)' : '0 4px 12px rgba(0,0,0,0.08)'
                            }
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75, gap: 0.5 }}>
                                <Box sx={{
                                    width: 20, height: 20, borderRadius: '6px',
                                    background: isBlacklisted
                                        ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: isBlacklisted
                                        ? '0 4px 12px rgba(239, 68, 68, 0.4)'
                                        : '0 4px 12px rgba(102, 126, 234, 0.3)'
                                }}>
                                    <EventIcon sx={{ color: 'white', fontSize: 12 }} />
                                </Box>
                                <Box>
                                    <Typography variant="h6" sx={{
                                        fontWeight: 700,
                                        color: isBlacklisted ? '#dc2626' : '#1a202c',
                                        fontSize: '0.8rem',
                                        letterSpacing: '-0.3px'
                                    }}>
                                        Event Details
                                    </Typography>
                                </Box>
                            </Box>
                            <Grid container spacing={1.5} rowSpacing={1}>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Event Name"
                                        value={formData.EventName}
                                        onChange={(e) => handleInputChange('EventName', e.target.value)}
                                        InputProps={{
                                            readOnly: mode === 'view'
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                height: { xs: '36px', sm: '28px' },
                                                fontSize: { xs: '0.875rem', sm: '0.75rem' },
                                                borderRadius: '8px'
                                            },
                                            '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '0.75rem' } },
                                            '& .MuiOutlinedInput-root': {
                                                '&:hover fieldset': {
                                                    borderColor: '#667eea',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#667eea',
                                                    borderWidth: '2px'
                                                }
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Event Location"
                                        value={formData.EventLocation}
                                        onChange={(e) => handleInputChange('EventLocation', e.target.value)}
                                        InputProps={{
                                            readOnly: mode === 'view'
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                height: { xs: '36px', sm: '28px' },
                                                fontSize: { xs: '0.875rem', sm: '0.75rem' },
                                                borderRadius: '8px'
                                            },
                                            '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '0.75rem' } },
                                            '& .MuiOutlinedInput-root': {
                                                '&:hover fieldset': {
                                                    borderColor: '#667eea',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#667eea',
                                                    borderWidth: '2px'
                                                }
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={1.2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Program Date"
                                        type="date"
                                        value={formData.ProgramDate}
                                        onChange={(e) => handleInputChange('ProgramDate', e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        InputProps={{
                                            readOnly: mode === 'view'
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                height: { xs: '36px', sm: '28px' },
                                                fontSize: { xs: '0.875rem', sm: '0.75rem' },
                                                borderRadius: '8px'
                                            },
                                            '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '0.75rem' } },
                                            '& .MuiOutlinedInput-root': {
                                                '&:hover fieldset': {
                                                    borderColor: '#667eea',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#667eea',
                                                    borderWidth: '2px'
                                                }
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={1.2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Delivery Date"
                                        type="date"
                                        value={formData.DeliveryDate}
                                        onChange={(e) => handleInputChange('DeliveryDate', e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        InputProps={{
                                            readOnly: mode === 'view'
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                height: { xs: '36px', sm: '28px' },
                                                fontSize: { xs: '0.875rem', sm: '0.75rem' },
                                                borderRadius: '8px'
                                            },
                                            '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '0.75rem' } },
                                            '& .MuiOutlinedInput-root': {
                                                '&:hover fieldset': {
                                                    borderColor: '#667eea',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#667eea',
                                                    borderWidth: '2px'
                                                }
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={1.2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Return Date"
                                        type="date"
                                        value={formData.DeliveryReturnDate}
                                        onChange={(e) => handleInputChange('DeliveryReturnDate', e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        InputProps={{
                                            readOnly: mode === 'view'
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                height: { xs: '36px', sm: '28px' },
                                                fontSize: { xs: '0.875rem', sm: '0.75rem' },
                                                borderRadius: '8px'
                                            },
                                            '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '0.75rem' } },
                                            '& .MuiOutlinedInput-root': {
                                                '&:hover fieldset': {
                                                    borderColor: '#667eea',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#667eea',
                                                    borderWidth: '2px'
                                                }
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={3} sm={0.9}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Event Days"
                                        type="number"
                                        value={formData.EventDays}
                                        onChange={(e) => handleInputChange('EventDays', e.target.value)}
                                        InputProps={{
                                            readOnly: mode === 'view'
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                height: { xs: '36px', sm: '28px' },
                                                fontSize: { xs: '0.875rem', sm: '0.75rem' },
                                                borderRadius: '8px'
                                            },
                                            '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '0.75rem' } },
                                            '& .MuiOutlinedInput-root': {
                                                '&:hover fieldset': {
                                                    borderColor: '#667eea',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#667eea',
                                                    borderWidth: '2px'
                                                }
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={3} sm={0.9}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Rent Days"
                                        type="number"
                                        value={formData.RentDays || ''}
                                        onChange={(e) => {
                                            const rentDays = e.target.value;
                                            // Update RentDays in formData and all items
                                            setFormData(prev => {
                                                const updatedItems = prev.items.map(item => ({
                                                    ...item,
                                                    RentDays: rentDays
                                                }));
                                                return {
                                                    ...prev,
                                                    RentDays: rentDays,
                                                    items: updatedItems
                                                };
                                            });
                                            // Recalculate all item amounts
                                            setTimeout(() => {
                                                formData.items.forEach((_, index) => {
                                                    calculateItemAmounts(index);
                                                });
                                            }, 0);
                                        }}
                                        InputProps={{
                                            readOnly: mode === 'view'
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                height: { xs: '36px', sm: '28px' },
                                                fontSize: { xs: '0.875rem', sm: '0.75rem' },
                                                borderRadius: '8px',
                                                bgcolor: '#fef3c7'
                                            },
                                            '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '0.75rem' }, color: '#f59e0b' },
                                            '& .MuiOutlinedInput-root': {
                                                '&:hover fieldset': {
                                                    borderColor: '#f59e0b',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#f59e0b',
                                                    borderWidth: '2px'
                                                }
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={1.6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Expected Time"
                                        type="time"
                                        value={formData.ExpectedTime}
                                        onChange={(e) => handleInputChange('ExpectedTime', e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        InputProps={{
                                            readOnly: mode === 'view'
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                height: { xs: '36px', sm: '28px' },
                                                fontSize: { xs: '0.875rem', sm: '0.75rem' },
                                                borderRadius: '8px'
                                            },
                                            '& .MuiInputBase-input': {
                                                color: '#000'
                                            },
                                            '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '0.75rem' }, color: '#000' },
                                            '& .MuiOutlinedInput-root': {
                                                '&:hover fieldset': {
                                                    borderColor: '#667eea',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#667eea',
                                                    borderWidth: '2px'
                                                }
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={10}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Event Address"
                                        value={formData.EventAddress}
                                        onChange={(e) => handleInputChange('EventAddress', e.target.value)}
                                        InputProps={{
                                            readOnly: mode === 'view'
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                height: { xs: '36px', sm: '28px' },
                                                fontSize: { xs: '0.875rem', sm: '0.75rem' },
                                                borderRadius: '8px'
                                            },
                                            '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '0.75rem' } },
                                            '& .MuiOutlinedInput-root': {
                                                '&:hover fieldset': {
                                                    borderColor: '#667eea',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#667eea',
                                                    borderWidth: '2px'
                                                }
                                            }
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    {/* Items Section */}
                    <Grid item xs={12}>
                        <Paper elevation={0} sx={{
                            p: 1,
                            border: isBlacklisted ? '2px solid #ef4444' : '1px solid #e2e8f0',
                            borderRadius: '8px',
                            bgcolor: isBlacklisted ? '#fef2f2' : 'white',
                            boxShadow: isBlacklisted ? '0 4px 12px rgba(239, 68, 68, 0.2)' : '0 1px 3px rgba(0,0,0,0.05)',
                            overflow: 'hidden',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                boxShadow: isBlacklisted ? '0 6px 16px rgba(239, 68, 68, 0.3)' : '0 4px 12px rgba(0,0,0,0.08)'
                            }
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{
                                        width: 20, height: 20, borderRadius: '6px',
                                        background: isBlacklisted
                                            ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                                            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: isBlacklisted
                                            ? '0 4px 12px rgba(239, 68, 68, 0.4)'
                                            : '0 4px 12px rgba(102, 126, 234, 0.3)'
                                    }}>
                                        <InventoryIcon sx={{ color: 'white', fontSize: 12 }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" sx={{
                                            fontWeight: 700,
                                            color: isBlacklisted ? '#dc2626' : '#1a202c',
                                            fontSize: '0.8rem',
                                            letterSpacing: '-0.3px'
                                        }}>
                                            Items
                                        </Typography>
                                    </Box>
                                </Box>
                                {mode !== 'view' && (
                                    <Button
                                        variant="contained"
                                        size="medium"
                                        startIcon={<ListIcon />}
                                        onClick={handleOpenGetItemsModal}
                                        sx={{
                                            borderRadius: '8px',
                                            background: isBlacklisted
                                                ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                                                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: '0.75rem',
                                            py: 0.5,
                                            px: 2.5,
                                            boxShadow: isBlacklisted
                                                ? '0 4px 12px rgba(239, 68, 68, 0.4)'
                                                : '0 4px 12px rgba(102, 126, 234, 0.3)',
                                            '&:hover': {
                                                background: isBlacklisted
                                                    ? 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)'
                                                    : 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                                                boxShadow: isBlacklisted
                                                    ? '0 6px 16px rgba(239, 68, 68, 0.5)'
                                                    : '0 6px 16px rgba(102, 126, 234, 0.4)',
                                                transform: 'translateY(-2px)'
                                            },
                                            transition: 'all 0.3s ease'
                                        }}
                                    >
                                        Get Items
                                    </Button>
                                )}
                            </Box>

                            {errors.items && (
                                <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
                                    {errors.items}
                                </Alert>
                            )}

                            <TableContainer component={Paper} sx={{
                                boxShadow: 'none',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                overflowX: 'auto',
                                overflowY: 'auto',
                                maxHeight: '400px'
                            }}>
                                <Table size="small" sx={{
                                    minWidth: '1050px',
                                    '& .MuiTableCell-root': {
                                        padding: '6px 10px',
                                        whiteSpace: 'nowrap'
                                    }
                                }}>
                                    <TableHead>
                                        <TableRow sx={{
                                            background: isBlacklisted
                                                ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                                                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                        }}>
                                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'white', border: 'none', minWidth: '200px', py: 1, px: 1.5 }}>Item</TableCell>
                                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'white', border: 'none', minWidth: '80px', textAlign: 'center', py: 1, px: 0.75 }}>Qty</TableCell>
                                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'white', border: 'none', minWidth: '80px', textAlign: 'center', py: 1, px: 0.75 }}>Rate</TableCell>
                                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'white', border: 'none', minWidth: '100px', textAlign: 'center', py: 1, px: 0.75 }}>Rent Days</TableCell>
                                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'white', border: 'none', minWidth: '90px', textAlign: 'right', py: 1, px: 1.5 }}>Amount</TableCell>
                                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'white', border: 'none', minWidth: '90px', textAlign: 'center', py: 1, px: 0.75 }}>Discount</TableCell>                                            
                                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'white', border: 'none', minWidth: '50px', textAlign: 'center', py: 1, px: 0.75 }}>GST%</TableCell>
                                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'white', border: 'none', minWidth: '90px', textAlign: 'right', py: 1, px: 1.5 }}>Taxable</TableCell>
                                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'white', border: 'none', minWidth: '90px', textAlign: 'right', py: 1, px: 1.5 }}>CGST</TableCell>
                                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'white', border: 'none', minWidth: '90px', textAlign: 'right', py: 1, px: 1.5 }}>SGST</TableCell>
                                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'white', border: 'none', minWidth: '100px', textAlign: 'right', py: 1, px: 1.5 }}>Total</TableCell>
                                            {mode !== 'view' && (
                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'white', border: 'none', minWidth: '50px', textAlign: 'center', py: 1, px: 0.75 }}>Action</TableCell>
                                            )}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {formData.items.length === 0 ? (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={mode === 'view' ? 12 : 13}
                                                    sx={{
                                                        textAlign: 'center',
                                                        py: 3,
                                                        border: 'none'
                                                    }}
                                                >
                                                    <Box sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        color: '#9ca3af'
                                                    }}>
                                                        <InventoryIcon sx={{ fontSize: 64, opacity: 0.3 }} />
                                                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#6b7280' }}>
                                                            No items added yet
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontSize: '0.75rem', opacity: 0.8 }}>
                                                            Click "Get Items" button to add items to this estimate
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            formData.items.map((item, index) => (
                                                <TableRow key={index} sx={{
                                                    '&:nth-of-type(even)': { backgroundColor: '#f9fafb' },
                                                    '&:hover': { backgroundColor: '#f3f4f6' },
                                                    transition: 'background-color 0.2s ease'
                                                }}>
                                                    <TableCell sx={{ border: 'none', borderBottom: '1px solid #e5e7eb', padding: '12px 16px' }}>
                                                        <Typography sx={{ fontSize: '0.75rem', color: '#1f2937', fontWeight: 600 }}>
                                                            {item.ItemName || 'N/A'}
                                                        </Typography>
                                                    </TableCell>

                                                    <TableCell sx={{ border: 'none', borderBottom: '1px solid #e5e7eb', padding: '8px', textAlign: 'center', minWidth: '100px' }}>
                                                        <TextField
                                                            fullWidth
                                                            type="number"
                                                            value={item.Quantity}
                                                            onChange={(e) => handleInputChange('Quantity', e.target.value, index)}
                                                            error={!!errors[`Quantity_${index}`]}
                                                            InputProps={{ readOnly: mode === 'view' }}
                                                            size="small"
                                                            sx={{
                                                                minWidth: '80px',
                                                                '& .MuiInputBase-input': {
                                                                    fontSize: '0.75rem',
                                                                    textAlign: 'right',
                                                                    py: 0.5,
                                                                    backgroundColor: mode === 'view' ? '#f9fafb' : 'white',
                                                                    borderRadius: '6px'
                                                                },
                                                                '& .MuiInputBase-root': {
                                                                    height: '32px'
                                                                },
                                                                '& .MuiOutlinedInput-root': {
                                                                    '&:hover fieldset': {
                                                                        borderColor: '#667eea',
                                                                    },
                                                                    '&.Mui-focused fieldset': {
                                                                        borderColor: '#667eea',
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ border: 'none', borderBottom: '1px solid #e5e7eb', padding: '8px', textAlign: 'center', minWidth: '120px' }}>
                                                        <TextField
                                                            fullWidth
                                                            type="number"
                                                            value={item.Rate}
                                                            onChange={(e) => handleInputChange('Rate', e.target.value, index)}
                                                            error={!!errors[`Rate_${index}`]}
                                                            InputProps={{ readOnly: mode === 'view' }}
                                                            size="small"
                                                            sx={{
                                                                minWidth: '100px',
                                                                '& .MuiInputBase-input': {
                                                                    fontSize: '0.75rem',
                                                                    textAlign: 'right',
                                                                    py: 0.5,
                                                                    backgroundColor: mode === 'view' ? '#f9fafb' : 'white',
                                                                    borderRadius: '6px'
                                                                },
                                                                '& .MuiInputBase-root': {
                                                                    height: '32px'
                                                                },
                                                                '& .MuiOutlinedInput-root': {
                                                                    '&:hover fieldset': {
                                                                        borderColor: '#667eea',
                                                                    },
                                                                    '&.Mui-focused fieldset': {
                                                                        borderColor: '#667eea',
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                    </TableCell>

                                                    {/* Rent Days Column */}
                                                    <TableCell sx={{ border: 'none', borderBottom: '1px solid #e5e7eb', padding: '12px', textAlign: 'center' }}>
                                                        <Chip
                                                            label={item.RentDays || formData.RentDays || '0'}
                                                            size="small"
                                                            sx={{
                                                                fontSize: '0.75rem',
                                                                fontWeight: 600,
                                                                bgcolor: '#fef3c7',
                                                                color: '#f59e0b'
                                                            }}
                                                        />
                                                    </TableCell>

                                                    {/* Amount Column (RentDays * Qty * Rate) */}
                                                    <TableCell sx={{ border: 'none', borderBottom: '1px solid #e5e7eb', padding: '12px 16px', textAlign: 'right' }}>
                                                        <Typography sx={{ fontSize: '0.75rem', color: '#059669', fontWeight: 600 }}>
                                                            ₹{(() => {
                                                                const qty = parseFloat(item.Quantity) || 0;
                                                                const rate = parseFloat(item.Rate) || 0;
                                                                const rentDays = parseFloat(item.RentDays || formData.RentDays) || 0;
                                                                return (rentDays * qty * rate).toFixed(2);
                                                            })()}
                                                        </Typography>
                                                    </TableCell>

                                                    {/* Discount Column */}
                                                    <TableCell sx={{ border: 'none', borderBottom: '1px solid #e5e7eb', padding: '8px', textAlign: 'center', minWidth: '120px' }}>
                                                        <TextField
                                                            fullWidth
                                                            type="number"
                                                            value={item.DiscountAmount}
                                                            onChange={(e) => handleInputChange('DiscountAmount', e.target.value, index)}
                                                            InputProps={{ readOnly: mode === 'view' }}
                                                            size="small"
                                                            sx={{
                                                                minWidth: '100px',
                                                                '& .MuiInputBase-input': {
                                                                    fontSize: '0.75rem',
                                                                    textAlign: 'right',
                                                                    py: 0.5,
                                                                    backgroundColor: mode === 'view' ? '#f9fafb' : 'white',
                                                                    borderRadius: '6px'
                                                                },
                                                                '& .MuiInputBase-root': {
                                                                    height: '32px'
                                                                },
                                                                '& .MuiOutlinedInput-root': {
                                                                    '&:hover fieldset': {
                                                                        borderColor: '#667eea',
                                                                    },
                                                                    '&.Mui-focused fieldset': {
                                                                        borderColor: '#667eea',
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                    </TableCell>
                                                    {/* <TableCell sx={{ border: 'none', borderBottom: '1px solid #e5e7eb', padding: '12px', textAlign: 'center' }}>
                                                        <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500 }}>
                                                            {item.HSNCode || '-'}
                                                        </Typography>
                                                    </TableCell> */}
                                                    <TableCell sx={{ border: 'none', borderBottom: '1px solid #e5e7eb', padding: '12px', textAlign: 'center' }}>
                                                        <Chip
                                                            label={`${item.GSTRate || '0'}%`}
                                                            size="small"
                                                            sx={{
                                                                fontSize: '0.75rem',
                                                                fontWeight: 600,
                                                                bgcolor: '#fef3c7',
                                                                color: '#92400e'
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ border: 'none', borderBottom: '1px solid #e5e7eb', padding: '12px 16px', textAlign: 'right' }}>
                                                        <Typography sx={{ fontSize: '0.75rem', color: '#1f2937', fontWeight: 600 }}>
                                                            ₹{item.TaxableAmount || '0'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ border: 'none', borderBottom: '1px solid #e5e7eb', padding: '12px 16px', textAlign: 'right' }}>
                                                        <Typography sx={{ fontSize: '0.75rem', color: '#1f2937', fontWeight: 500 }}>
                                                            ₹{item.CGSTAmount || '0'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ border: 'none', borderBottom: '1px solid #e5e7eb', padding: '12px 16px', textAlign: 'right' }}>
                                                        <Typography sx={{ fontSize: '0.75rem', color: '#1f2937', fontWeight: 500 }}>
                                                            ₹{item.SGSTAmount || '0'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ border: 'none', borderBottom: '1px solid #e5e7eb', padding: '12px 16px', textAlign: 'right' }}>
                                                        <Typography sx={{ fontSize: '0.75rem', color: '#059669', fontWeight: 700 }}>
                                                            ₹{item.TotalAmount || '0'}
                                                        </Typography>
                                                    </TableCell>
                                                    {mode !== 'view' && (
                                                        <TableCell sx={{ border: 'none', borderBottom: '1px solid #e5e7eb', padding: '8px', textAlign: 'center' }}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => removeItem(index)}
                                                                sx={{
                                                                    color: '#ef4444',
                                                                    bgcolor: '#fef2f2',
                                                                    width: 32,
                                                                    height: 32,
                                                                    '&:hover': {
                                                                        backgroundColor: '#fee2e2',
                                                                        color: '#dc2626',
                                                                        transform: 'scale(1.1)'
                                                                    },
                                                                    transition: 'all 0.2s ease'
                                                                }}
                                                            >
                                                                <DeleteIcon sx={{ fontSize: 18 }} />
                                                            </IconButton>
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>


                    {/* Calculation Section */}
                    <Grid item xs={12}>
                        <Paper elevation={0} sx={{
                            p: 1,
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            bgcolor: 'white',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                            }
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75, gap: 0.5 }}>
                                <Box sx={{
                                    width: 20, height: 20, borderRadius: '6px',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                                }}>
                                    <PriceIcon sx={{ color: 'white', fontSize: 12 }} />
                                </Box>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a202c', fontSize: '0.8rem', letterSpacing: '-0.3px' }}>
                                        Calculation Summary
                                    </Typography>
                                </Box>
                            </Box>
                            <Grid container spacing={1}>
                                <Grid item xs={6} sm={1.2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Amount"
                                        value={`₹${formData.Amount}`}
                                        disabled
                                        InputProps={{
                                            readOnly: true,
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <PriceIcon sx={{ color: '#059669', fontSize: { xs: 20, sm: 18 } }} />
                                                </InputAdornment>
                                            )
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                height: { xs: '36px', sm: '28px' },
                                                fontSize: { xs: '0.875rem', sm: '0.75rem' },
                                                bgcolor: '#f0fdf4',
                                                borderRadius: '8px',
                                                fontWeight: 600
                                            },
                                            '& .MuiInputBase-input': {
                                                textAlign: 'right',
                                                color: '#000',
                                                WebkitTextFillColor: '#000'
                                            },
                                            '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '0.75rem' }, color: '#000' }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={1.2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Discount"
                                        value={`₹${formData.DiscountAmount}`}
                                        disabled
                                        InputProps={{
                                            readOnly: true
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                height: { xs: '36px', sm: '28px' },
                                                fontSize: { xs: '0.875rem', sm: '0.75rem' },
                                                bgcolor: '#fef3c7',
                                                borderRadius: '8px',
                                                fontWeight: 600
                                            },
                                            '& .MuiInputBase-input': {
                                                textAlign: 'right',
                                                color: '#000',
                                                WebkitTextFillColor: '#000'
                                            },
                                            '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '0.75rem' }, color: '#000' }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={1.2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="CGST"
                                        value={`₹${formData.CGSTAmount}`}
                                        disabled
                                        InputProps={{
                                            readOnly: true
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                height: { xs: '36px', sm: '28px' },
                                                fontSize: { xs: '0.875rem', sm: '0.75rem' },
                                                bgcolor: '#f7fafc',
                                                borderRadius: '8px',
                                                fontWeight: 600
                                            },
                                            '& .MuiInputBase-input': {
                                                textAlign: 'right',
                                                color: '#000',
                                                WebkitTextFillColor: '#000'
                                            },
                                            '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '0.75rem' }, color: '#000' }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={1.2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="SGST"
                                        value={`₹${formData.SGSTAmount}`}
                                        disabled
                                        InputProps={{
                                            readOnly: true
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                height: { xs: '36px', sm: '28px' },
                                                fontSize: { xs: '0.875rem', sm: '0.75rem' },
                                                bgcolor: '#f7fafc',
                                                borderRadius: '8px',
                                                fontWeight: 600
                                            },
                                            '& .MuiInputBase-input': {
                                                textAlign: 'right',
                                                color: '#000',
                                                WebkitTextFillColor: '#000'
                                            },
                                            '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '0.75rem' }, color: '#000' }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={1.2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="IGST"
                                        value={`₹${formData.IGSTAmount}`}
                                        disabled
                                        InputProps={{
                                            readOnly: true
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                height: { xs: '36px', sm: '28px' },
                                                fontSize: { xs: '0.875rem', sm: '0.75rem' },
                                                bgcolor: '#f7fafc',
                                                borderRadius: '8px',
                                                fontWeight: 600
                                            },
                                            '& .MuiInputBase-input': {
                                                textAlign: 'right',
                                                color: '#000',
                                                WebkitTextFillColor: '#000'
                                            },
                                            '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '0.75rem' }, color: '#000' }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={1.2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Transport Charge"
                                        type="number"
                                        value={formData.TransportCharge}
                                        onChange={(e) => handleInputChange('TransportCharge', e.target.value)}
                                        InputProps={{
                                            readOnly: mode === 'view',
                                            startAdornment: (
                                                <InputAdornment position="start">₹</InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                height: { xs: '36px', sm: '28px' },
                                                fontSize: { xs: '0.875rem', sm: '0.75rem' },
                                                borderRadius: '8px'
                                            },
                                            '& .MuiInputBase-input': {
                                                textAlign: 'right',
                                                color: '#000'
                                            },
                                            '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '0.75rem' }, color: '#000' },
                                            '& .MuiOutlinedInput-root': {
                                                '&:hover fieldset': {
                                                    borderColor: '#667eea',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#667eea',
                                                    borderWidth: '2px'
                                                }
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={1.2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Round Off"
                                        type="number"
                                        value={formData.RoundOff}
                                        onChange={(e) => handleInputChange('RoundOff', e.target.value)}
                                        InputProps={{
                                            readOnly: mode === 'view',
                                            startAdornment: (
                                                <InputAdornment position="start">₹</InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                height: { xs: '36px', sm: '28px' },
                                                fontSize: { xs: '0.875rem', sm: '0.75rem' },
                                                borderRadius: '8px'
                                            },
                                            '& .MuiInputBase-input': {
                                                textAlign: 'right',
                                                color: '#000'
                                            },
                                            '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '0.75rem' }, color: '#000' },
                                            '& .MuiOutlinedInput-root': {
                                                '&:hover fieldset': {
                                                    borderColor: '#667eea',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#667eea',
                                                    borderWidth: '2px'
                                                }
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={1.2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Total Amount"
                                        value={`₹${formData.TotalAmount}`}
                                        disabled
                                        InputProps={{
                                            readOnly: true,
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <CheckCircleIcon sx={{ color: '#059669', fontSize: { xs: 20, sm: 18 } }} />
                                                </InputAdornment>
                                            )
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                height: { xs: '36px', sm: '28px' },
                                                fontSize: { xs: '0.95rem', sm: '0.85rem' },
                                                bgcolor: '#dcfce7',
                                                borderRadius: '8px',
                                                fontWeight: 700,
                                                color: '#059669'
                                            },
                                            '& .MuiInputBase-input': {
                                                textAlign: 'right',
                                                color: '#000',
                                                WebkitTextFillColor: '#000'
                                            },
                                            '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '0.75rem' }, fontWeight: 600, color: '#000' }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={1.2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Advance Amount"
                                        type="number"
                                        value={formData.AdvanceAmount}
                                        onChange={(e) => handleInputChange('AdvanceAmount', e.target.value)}
                                        InputProps={{
                                            readOnly: mode === 'view',
                                            startAdornment: (
                                                <InputAdornment position="start">₹</InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                height: { xs: '36px', sm: '28px' },
                                                fontSize: { xs: '0.875rem', sm: '0.75rem' },
                                                borderRadius: '8px'
                                            },
                                            '& .MuiInputBase-input': {
                                                textAlign: 'right',
                                                color: '#000'
                                            },
                                            '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '0.75rem' }, color: '#000' },
                                            '& .MuiOutlinedInput-root': {
                                                '&:hover fieldset': {
                                                    borderColor: '#667eea',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#667eea',
                                                    borderWidth: '2px'
                                                }
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={1.2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Balance Amount"
                                        value={`₹${formData.BalanceAmount}`}
                                        disabled
                                        InputProps={{
                                            readOnly: true
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                height: { xs: '36px', sm: '28px' },
                                                fontSize: { xs: '0.875rem', sm: '0.75rem' },
                                                bgcolor: '#f7fafc',
                                                borderRadius: '8px',
                                                fontWeight: 600
                                            },
                                            '& .MuiInputBase-input': {
                                                textAlign: 'right',
                                                color: '#000',
                                                WebkitTextFillColor: '#000'
                                            },
                                            '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '0.75rem' }, color: '#000' }
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    {/* Remarks Section */}
                    <Grid item xs={12}>
                        <Paper elevation={0} sx={{
                            p: 1,
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            bgcolor: 'white',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                            }
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75, gap: 0.5 }}>
                                <Box sx={{
                                    width: 20, height: 20, borderRadius: '6px',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                                }}>
                                    <NotesIcon sx={{ color: 'white', fontSize: 12 }} />
                                </Box>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a202c', fontSize: '0.8rem', letterSpacing: '-0.3px' }}>
                                        Remarks
                                    </Typography>
                                </Box>
                            </Box>

                            <TextField
                                fullWidth
                                label="Remarks"
                                multiline
                                rows={3}
                                value={formData.Remarks}
                                onChange={(e) => handleInputChange('Remarks', e.target.value)}
                                InputProps={{ readOnly: mode === 'view' }}
                                placeholder="Enter any additional remarks or special instructions..."
                                size="small"
                                sx={{
                                    '& .MuiInputLabel-root': { fontSize: '0.75rem' },
                                    '& .MuiInputBase-input': { fontSize: '0.75rem', lineHeight: 1.6 },
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                        '&:hover fieldset': {
                                            borderColor: '#667eea',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#667eea',
                                            borderWidth: '2px'
                                        }
                                    }
                                }}
                            />
                        </Paper>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ 
                p: { xs: 0.75, sm: 1 }, 
                gap: { xs: 0.75, sm: 1 }, 
                bgcolor: '#f8fafc', 
                borderTop: '1px solid #e2e8f0',
                flexDirection: { xs: 'column', sm: 'row' },
                '& > button': { width: { xs: '100%', sm: 'auto' } }
            }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    disabled={loading}
                    sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: { xs: '0.85rem', sm: '0.75rem' },
                        py: { xs: 0.75, sm: 0.5 },
                        px: 3,
                        borderColor: '#cbd5e0',
                        color: '#4a5568',
                        '&:hover': {
                            borderColor: '#a0aec0',
                            bgcolor: '#edf2f7'
                        }
                    }}
                >
                    {mode === 'view' ? 'Close' : 'Cancel'}
                </Button>
                {mode !== 'view' && (
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
                        sx={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: { xs: '0.85rem', sm: '0.75rem' },
                            py: { xs: 0.75, sm: 0.5 },
                            px: 3,
                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                                boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
                                transform: 'translateY(-2px)'
                            },
                            '&:disabled': {
                                background: '#cbd5e0',
                                color: '#a0aec0'
                            },
                            transition: 'all 0.3s ease'
                        }}
                    >
                        {loading ? (mode === 'edit' ? 'Updating...' : 'Saving...') : (mode === 'edit' ? 'Update Estimate' : 'Save Estimate')}
                    </Button>
                )}
            </DialogActions>

            {/* Success/Error Message Snackbar */}
            <Snackbar
                open={showSuccessMessage}
                autoHideDuration={3000}
                onClose={() => setShowSuccessMessage(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setShowSuccessMessage(false)}
                    severity={successMessage.includes('successfully') ? 'success' : 'error'}
                    sx={{ width: '100%' }}
                >
                    {successMessage}
                </Alert>
            </Snackbar>

            {/* Get Items Modal */}
            <Dialog
                open={getItemsModalOpen}
                onClose={handleCloseGetItemsModal}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '6px',
                        m: 0.5,
                        maxHeight: '75vh',
                        width: '85%',
                        maxWidth: '1000px'
                    }
                }}
            >
                <DialogTitle sx={{
                    py: 0.5,
                    px: 1.5,
                    borderBottom: '1px solid #d1d5db',
                    bgcolor: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                    color: '#fff'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ListIcon sx={{ color: '#fff', fontSize: 16 }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff' }}>
                                Get Items ({getFilteredItems().length} available)
                            </Typography>
                        </Box>
                        <IconButton onClick={handleCloseGetItemsModal} size="small" sx={{ color: '#fff', p: 0.25 }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ p: 1, bgcolor: '#f8fafc' }}>
                    {getItemsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '150px' }}>
                            <CircularProgress size={20} />
                            <Typography variant="body2" sx={{ ml: 1.5, color: '#6b7280', fontSize: '0.75rem' }}>Loading items...</Typography>
                        </Box>
                    ) : (
                        <>
                            {/* Search */}
                            <Box sx={{ mb: 1.5 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Search items by name, HSN code, or item code..."
                                    value={getItemsSearchValue}
                                    onChange={(e) => handleGetItemsSearch(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ fontSize: 14, color: '#6b7280' }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: getItemsSearchValue && (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleGetItemsSearch('')}
                                                    sx={{ p: 0.25 }}
                                                >
                                                    <CloseIcon sx={{ fontSize: 12 }} />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                    sx={{
                                        '& .MuiInputBase-input': { py: 0.5, fontSize: '0.75rem' },
                                        '& .MuiOutlinedInput-root': { bgcolor: '#fff' }
                                    }}
                                />
                            </Box>

                            {/* Selected Items Summary */}
                            {selectedItems.length > 0 && (
                                <Box sx={{ mb: 1.5, p: 1, bgcolor: '#eff6ff', borderRadius: 1, border: '1px solid #bfdbfe' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af', mb: 0.5, display: 'block' }}>
                                        Selected Items ({selectedItems.length})
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selectedItems.slice(0, 8).map(item => (
                                            <Chip
                                                key={item.Id}
                                                label={`${item.Name} (${item.selectedQuantity})`}
                                                size="small"
                                                onDelete={() => handleItemSelection(item, false)}
                                                sx={{ fontSize: '0.65rem', height: '20px' }}
                                            />
                                        ))}
                                        {selectedItems.length > 8 && (
                                            <Chip label={`+${selectedItems.length - 8} more`} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: '20px' }} />
                                        )}
                                    </Box>
                                </Box>
                            )}

                            {/* Items Table */}
                            <Box sx={{ border: '1px solid #d1d5db', borderRadius: 1, overflow: 'hidden', bgcolor: '#fff' }}>
                                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                                        <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                            <tr style={{ background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', height: '28px' }}>
                                                <th style={{ padding: '3px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '5%' }}>
                                                    <Checkbox
                                                        size="small"
                                                        checked={selectedItems.length === getPaginatedItems().length && getPaginatedItems().length > 0}
                                                        indeterminate={selectedItems.length > 0 && selectedItems.length < getPaginatedItems().length}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                getPaginatedItems().forEach(item => {
                                                                    if (!selectedItems.find(si => si.Id === item.Id)) {
                                                                        handleItemSelection(item, true);
                                                                    }
                                                                });
                                                            } else {
                                                                getPaginatedItems().forEach(item => {
                                                                    handleItemSelection(item, false);
                                                                });
                                                            }
                                                        }}
                                                        sx={{ color: '#fff', '&.Mui-checked': { color: '#fff' } }}
                                                    />
                                                </th>
                                                <th style={{ padding: '3px 6px', textAlign: 'left', fontWeight: 600, color: '#fff', width: '25%', fontSize: '0.65rem' }}>Item</th>
                                                <th style={{ padding: '3px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '10%', fontSize: '0.65rem' }}>HSN</th>
                                                <th style={{ padding: '3px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '8%', fontSize: '0.65rem' }}>Qty</th>
                                                <th style={{ padding: '3px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '12%', fontSize: '0.65rem' }}>Rate</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {getPaginatedItems().map((item, index) => {
                                                const selectedItem = selectedItems.find(si => si.Id === item.Id);
                                                const isSelected = !!selectedItem;
                                                const isAlreadyAdded = isItemAlreadyInEstimate(item.Id);

                                                return (
                                                    <tr key={item.Id} style={{
                                                        borderBottom: '1px solid #e5e7eb',
                                                        background: isSelected ? '#eff6ff' : (index % 2 === 0 ? '#fff' : '#f9fafb'),
                                                        opacity: isAlreadyAdded ? 0.5 : 1,
                                                        height: '30px'
                                                    }}>
                                                        <td style={{ padding: '3px', textAlign: 'center' }}>
                                                            <Checkbox
                                                                size="small"
                                                                checked={isSelected}
                                                                disabled={isAlreadyAdded}
                                                                onChange={(e) => handleItemSelection(item, e.target.checked)}
                                                            />
                                                        </td>
                                                        <td style={{ padding: '3px 6px', fontWeight: 500, fontSize: '0.65rem' }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <span>{item.Name}</span>
                                                                {isAlreadyAdded && (
                                                                    <Chip label="Added" size="small" color="success" sx={{ fontSize: '0.55rem', height: 12 }} />
                                                                )}
                                                            </Box>
                                                        </td>
                                                        <td style={{ padding: '3px 4px', textAlign: 'center', fontSize: '0.65rem', color: '#6b7280' }}>{item.HSNCode || '-'}</td>

                                                        <td style={{ padding: '3px 4px', textAlign: 'center' }}>
                                                            {isSelected ? (
                                                                <TextField
                                                                    size="small"
                                                                    type="number"
                                                                    value={selectedItem.selectedQuantity}
                                                                    onChange={(e) => handleSelectedItemChange(item.Id, 'selectedQuantity', e.target.value)}
                                                                    inputProps={{ min: 1 }}
                                                                    sx={{
                                                                        width: 45,
                                                                        '& .MuiInputBase-input': {
                                                                            py: 0.15,
                                                                            px: 0.15,
                                                                            fontSize: '0.65rem',
                                                                            textAlign: 'center',
                                                                            color: '#000'
                                                                        }
                                                                    }}
                                                                />
                                                            ) : (
                                                                <span style={{ color: '#9ca3af', fontSize: '0.65rem' }}>1</span>
                                                            )}
                                                        </td>
                                                        <td style={{ padding: '3px 4px', textAlign: 'right' }}>
                                                            {isSelected ? (
                                                                <TextField
                                                                    size="small"
                                                                    type="number"
                                                                    value={selectedItem.selectedRate}
                                                                    onChange={(e) => handleSelectedItemChange(item.Id, 'selectedRate', e.target.value)}
                                                                    inputProps={{ min: 0 }}
                                                                    sx={{
                                                                        width: 65,
                                                                        '& .MuiInputBase-input': {
                                                                            py: 0.15,
                                                                            px: 0.15,
                                                                            fontSize: '0.65rem',
                                                                            textAlign: 'right',
                                                                            color: '#000'
                                                                        }
                                                                    }}
                                                                />
                                                            ) : (
                                                                <span style={{ color: '#9ca3af', fontSize: '0.65rem' }}>
                                                                    ₹{(item.EventSaleRate || item.Rate || 0).toFixed(2)}
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            {getPaginatedItems().length === 0 && (
                                                <tr>
                                                    <td colSpan="7" style={{ padding: '12px', textAlign: 'center', color: '#6b7280', fontSize: '0.75rem' }}>
                                                        {getItemsSearchValue ? 'No items found matching your search' : 'No items available'}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </Box>
                            </Box>

                            {/* Pagination */}
                            {getTotalPages > 1 && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Button
                                            size="small"
                                            onClick={() => setCurrentItemsPage(prev => Math.max(1, prev - 1))}
                                            disabled={currentItemsPage === 1}
                                            sx={{ fontSize: '0.75rem', py: 0.25, px: 0.75 }}
                                        >
                                            Previous
                                        </Button>
                                        <Typography variant="caption" sx={{ mx: 1.5 }}>
                                            Page {currentItemsPage} of {getTotalPages}
                                        </Typography>
                                        <Button
                                            size="small"
                                            onClick={() => setCurrentItemsPage(prev => Math.min(getTotalPages, prev + 1))}
                                            disabled={currentItemsPage === getTotalPages}
                                            sx={{ fontSize: '0.75rem', py: 0.25, px: 0.75 }}
                                        >
                                            Next
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 1.5, py: 0.5, borderTop: '1px solid #d1d5db', bgcolor: '#f8fafc', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ color: '#6b7280' }}>
                        {selectedItems.length > 0 && `${selectedItems.length} item${selectedItems.length > 1 ? 's' : ''} selected`}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            onClick={handleCloseGetItemsModal}
                            variant="outlined"
                            size="small"
                            sx={{ fontSize: '0.75rem', py: 0.25, px: 1.5 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleAddSelectedItems}
                            variant="contained"
                            size="small"
                            disabled={selectedItems.length === 0 || addingItemsLoading}
                            sx={{
                                bgcolor: '#1e40af',
                                '&:hover': { bgcolor: '#1d4ed8' },
                                fontSize: '0.75rem',
                                py: 0.25,
                                px: 1.5
                            }}
                        >
                            {addingItemsLoading ? (
                                <>
                                    <CircularProgress size={12} sx={{ mr: 0.5 }} />
                                    Adding...
                                </>
                            ) : (
                                `Add Selected (${selectedItems.length})`
                            )}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>

            {/* Success Dialog */}
            <Dialog
                open={showSuccessDialog}
                onClose={() => setShowSuccessDialog(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        textAlign: 'center',
                        p: 2
                    }
                }}
            >
                <DialogContent sx={{ pt: 3, pb: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{
                            bgcolor: '#10b981',
                            width: 64,
                            height: 64,
                            mb: 1
                        }}>
                            <CheckCircleIcon sx={{ fontSize: 32, color: 'white' }} />
                        </Avatar>
                        <Typography variant="h5" sx={{
                            fontWeight: 600,
                            color: '#065f46',
                            mb: 1
                        }}>
                            Success!
                        </Typography>
                        <Typography variant="body1" sx={{
                            color: '#374151',
                            fontSize: '0.9rem'
                        }}>
                            {successMessage}
                        </Typography>
                        <Typography variant="body2" sx={{
                            color: '#6b7280',
                            fontSize: '0.75rem',
                            mt: 1
                        }}>
                            This dialog will close automatically in 3 seconds
                        </Typography>
                    </Box>
                </DialogContent>
            </Dialog>
        </Dialog >
    );
};

export default EstimateModal;
















